import { z } from "zod";

import { Meta, getParsedData } from "./getParsedData";

import { DownloadResourceType } from "@/components/TeacherComponents/types/downloadAndShare.types";
import OakError from "@/errors/OakError";
import getBrowserConfig from "@/browser-lib/getBrowserConfig";
import { teachWithOakShortReadsExistenceSchema } from "@/components/TeacherComponents/hooks/downloadAndShareHooks/teachWithOakShortReads.schema";

const DOWNLOADS_API_URL = getBrowserConfig("downloadApiUrl");

/**
 * PUPIL-1750: Keep downloads API "check-files" calls client-side only.
 * Calling these during SSR / ISR (including revalidate) has repeatedly been rolled back
 * due to high-volume failures/noise and unreliable upstream behaviour under revalidation load.
 */

/**
 * Expected response schema
 */
export const lessonDataSchema = z.object({
  resources: z.array(
    z.tuple([
      z.custom<DownloadResourceType>(),
      z.object({
        exists: z.boolean(),
        errors: z
          .array(
            z.object({
              message: z.string(),
            }),
          )
          .optional(),
        fileSize: z.string().optional(),
        ext: z.string().optional(),
      }),
    ]),
  ),
});
const unitDataSchema = z.object({
  exists: z.boolean(),
  fileSize: z.string().optional(),
});

const schema = z.object({
  data: lessonDataSchema
    .or(unitDataSchema)
    .or(teachWithOakShortReadsExistenceSchema),
  error: z
    .object({
      message: z.string(),
    })
    .optional(),
});

export type DownloadsApiCheckFilesResponseSchema = z.infer<typeof schema>;

const getDownloadExistence = async (
  checkWhichResourcesExistEndpoint: string,
  meta?: Meta,
) => {
  const res = await fetch(checkWhichResourcesExistEndpoint);

  if (!res.ok) {
    throw new OakError({
      code: "downloads/check-files-failed",
      meta: {
        ...meta,
        errorSource: "getDownloadExistence",
      },
    });
  }

  const json = await res.json();

  const data = getParsedData<z.infer<typeof schema>>(
    json,
    schema,
    "downloads/check-files-failed",
    meta,
  );

  return data;
};

export const getLessonDownloadResourcesExistence = async ({
  lessonSlug,
  resourceTypesString,
  additionalFilesIdsString,
  isLegacyDownload,
}: {
  lessonSlug: string;
  resourceTypesString: string;
  additionalFilesIdsString?: string;
  isLegacyDownload: boolean;
}) => {
  const checkWhichResourcesExistEndpoint = additionalFilesIdsString
    ? `${DOWNLOADS_API_URL}/api/lesson/${lessonSlug}/check-files?selection=${resourceTypesString}&additionalFiles=${additionalFilesIdsString}`
    : `${DOWNLOADS_API_URL}/api/lesson/${lessonSlug}/check-files?selection=${resourceTypesString}`;

  const meta = {
    lessonSlug,
    resourceTypesString,
    isLegacyDownload,
  };

  const res = await getDownloadExistence(
    checkWhichResourcesExistEndpoint,
    meta,
  );
  return lessonDataSchema.parse(res);
};

export const getUnitDownloadFileExistence = async (unitFileId: string) => {
  const checkWhichResourcesExistEndpoint = `${DOWNLOADS_API_URL}/api/unit/${unitFileId}/check-files`;

  const meta = {
    unitProgrammeSlug: unitFileId,
  };

  const res = await getDownloadExistence(
    checkWhichResourcesExistEndpoint,
    meta,
  );
  return unitDataSchema.parse(res);
};

export const getTeachWithOakDownloadFileExistence = async () => {
  const checkWhichResourcesExistEndpoint = `${DOWNLOADS_API_URL}/api/teach-with-oak/short-reads/check-files`;
  const res = await getDownloadExistence(checkWhichResourcesExistEndpoint);
  return teachWithOakShortReadsExistenceSchema.parse(res);
};
